import * as Phaser from "phaser-ce"
import { CCUtils } from "../../../common/CCUtils"
import { COLOR } from "../../../common/Color"
import { log } from "../../../common/Logging"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { ButtonContainer, Buttons, ButtonSize, ButtonType } from "../../generic-sprites/Buttons"
import { DebugPanel } from "../../generic-sprites/DebugPanel"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { ColorUtils } from "../../utils/ColorUtils"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale } from "../../utils/Scale"
import { Transitions } from "../transitions/Transitions"
import { MakeAVideoStore } from "./MakeAVideoStore"
import { loadAssets, MakeVideoAudio } from "./MakeVideoAssets"
import { MakeVideoSpriteTypes } from "./MakeVideoTypes"
import { MakeAVideoTitleSprite } from "./sprites/MakeAVideoTitleSprite"
import { MakeVideoBackgroundSelectContainer } from "./sprites/MakeVideoBackgroundSelectContainer"
import { MakeVideoCheeringCrowdSprite } from "./sprites/MakeVideoCheeringCrowdSprite"
import { MakeVideoClipPreviewContainer } from "./sprites/MakeVideoClipPreviewContainer"
import { MakeVideoIntroBandSprite } from "./sprites/MakeVideoIntroBandSprite"
import { MakeVideoIntroTateSprite } from "./sprites/MakeVideoIntroTateSprite"
import { MakeVideoMusicButtonContainer } from "./sprites/MakeVideoMusicButtonContainer"
import { MakeVideoScreenText } from "./sprites/MakeVideoScreenText"
import { MakeVideoSpriteCreator } from "./sprites/MakeVideoSpriteCreator"
import { MakeVideoStepsContainer } from "./sprites/MakeVideoStepsContainer"
import { MakeVideoThoughtBubbleSprite } from "./sprites/MakeVideoThoughtBubbleSprite"
import { MakeVideoTitleSelectContainer } from "./sprites/MakeVideoTitleSelectContainer"
import { MakeVideoVideoPlayerContainer } from "./sprites/MakeVideoVideoPlayerContainer"
import { MakeVideoVideoTimeline } from "./sprites/MakeVideoVideoTimeline"

export class MakeAVideoState extends Phaser.State {

    static debugPanel: DebugPanel
    stepContainer: MakeVideoStepsContainer
    screenText: MakeVideoScreenText
    nextButton: ButtonContainer
    editButton: ButtonContainer
    private sprites: MakeVideoSpriteCreator
    private current = -1
    private edit: boolean

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        MakeAVideoStore.newInstance()
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setPageBackgroundColor(COLOR.black)
        ColorUtils.setGameBackgroundColor(this.game, "#a3e0db")
        MakeAVideoState.debugPanel = new DebugPanel(this.game)

        this.sprites = new MakeVideoSpriteCreator(this.game, this.moveToStep)
        this.edit = false
        this.introSequence()
    }

    get step(): number {
        return this.current
    }

    moveToStep = (s?: number) => {
        this.game.sound.stopAll()
        if (s == null) {
            s = this.current + 1
        }
        if (s === 5) {
            // Clicked next on the review screen, go to outro!
            Transitions.startOutro(this.game, ActivityKey.MAKE_A_VIDEO)
            return
        }
        if (!this.stepContainer || !this.screenText || !this.nextButton) {
            this.setScreenComponents()
        }

        if (this.editButton) {
            this.editButton.destroy()
        }

        this.nextButton.disable()

        this.current = s
        this.sprites.destroyAll()

        switch (s) {
            case 0:
                this.chooseMusicScreen()
                break
            case 1:
                this.chooseVideoScreen()
                break
            case 2:
                this.chooseTitleScreen()
                break
            case 3:
                this.chooseBackgroundScreen()
                break
            case 4:
                this.reviewVideo()
                break
            default:
                throw new Error("No step for this step: " + s)
        }
        if (this.stepContainer && this.screenText) {
            this.stepContainer.step = s
            this.screenText.step = {step: s, edit: this.edit}
        }
    }

    private introSequence = () => {
        const backgroundGroup = this.game.add.group()
        const tateGroup = this.game.add.group()
        const titleGroup = this.game.add.group()

        const tate = this.sprites.create(MakeVideoSpriteTypes.INTRO_TATE, {
            x: Scale.x(0.422),
            y: Scale.y(0.035),
            group: tateGroup
        }) as MakeVideoIntroTateSprite
        const band = this.sprites.create(MakeVideoSpriteTypes.INTRO_BAND, {
            x: Scale.x(-1.5),
            y: Scale.y(0.019),
            group: backgroundGroup,
            tweenX: Scale.x(0.025)
        }) as MakeVideoIntroBandSprite
        const crowd = this.sprites.create(MakeVideoSpriteTypes.CHEERING_CROWD, {
            x: Scale.x(0.035),
            y: Scale.y(0.646),
            group: backgroundGroup
        }) as MakeVideoCheeringCrowdSprite
        const thought = this.sprites.create(MakeVideoSpriteTypes.THOUGHT_BUBBLE, {
            x: Scale.x(0.03),
            y: Scale.y(0.015),
            group: backgroundGroup
        }) as MakeVideoThoughtBubbleSprite
        const title = this.sprites.create(MakeVideoSpriteTypes.TITLE_SPRITE, {x: Scale.x(0.065), y: Scale.y(0.05), group: titleGroup}) as MakeAVideoTitleSprite
        const button = Buttons.create(this.game, "Next",
            () => {
                this.moveToStep(0)
                button.destroy()
            },
            {
                buttonSize: ButtonSize.WIDE,
                buttonType: ButtonType.PRIMARY,
                x: Scale.x(0.25),
                y: Scale.y(0.49)
            })
        button.setInvisible()
        button.disable()

        const timer = this.game.time.create(true)
        timer.add(3000, band.tweenIn)
        timer.add(5000, crowd.cheer)
        timer.add(7000, crowd.destroy)
        timer.add(7000, band.destroy)
        timer.add(8000, tate.smokingCig)
        timer.add(9000, thought.start)
        timer.add(11000, thought.destroy)
        timer.add(12000, () => title.visible = true)
        timer.start()

        const audio = this.game.add.audio(MakeVideoAudio.INTRO)
        audio.onPlay.addOnce(() => timer.start())
        audio.onStop.addOnce(() => {
            button.setVisible()
            button.enable()
        })
        audio.play()

        MakeAVideoState.debugPanel.setSkip(() => {
            audio.onStop.removeAll()
            audio.stop()
            band.destroy()
            crowd.destroy()
            thought.destroy()
            button.destroy()
            titleGroup.destroy()
            tateGroup.destroy()
            this.moveToStep(0)
        })
    }

    private chooseMusicScreen = () => {
        log.debug("Choose music screen.")
        const buttons = this.sprites.create(MakeVideoSpriteTypes.MUSIC_BUTTONS, {
            x: Scale.x(0.105),
            y: Scale.y(0.5),
            nextButton: this.nextButton,
            edit: this.edit
        }) as MakeVideoMusicButtonContainer
        log.debug("Choose music screen get audio.")
        this.getScreenAudio(buttons.disableAll, buttons.enableAll)
    }

    private chooseVideoScreen = () => {
        log.debug("Choose video screen.")
        const timeline = this.sprites.create(MakeVideoSpriteTypes.VIDEO_TIMELINE, {
            x: Scale.x(0.026),
            y: Scale.y(0.75)
        }) as MakeVideoVideoTimeline

        const clips = this.sprites.create(MakeVideoSpriteTypes.CLIP_PREVIEW, {
            x: Scale.x(0.42),
            y: Scale.y(0.4),
            sprites: timeline.tiles,
            nextButton: this.nextButton,
            edit: this.edit
        }) as MakeVideoClipPreviewContainer

        this.getScreenAudio(clips.disableAll, clips.enableAll)
    }

    private chooseTitleScreen = () => {
        log.debug("Choose title screen.")
        const chooseTitle = this.game.add.audio(MakeVideoAudio.CHOOSE_TITLE_SCREEN)
        const titles = this.sprites.create(MakeVideoSpriteTypes.TITLE_SELECTION, {
            x: 0,
            y: Scale.y(0.28),
            nextButton: this.nextButton,
            edit: this.edit
        }) as MakeVideoTitleSelectContainer

        if (this.edit) {
            this.getScreenAudio(titles.disableAll, titles.enableAll)
        } else {
            chooseTitle.onPlay.addOnce(titles.disableAll)
            chooseTitle.onStop.addOnce(() => this.getScreenAudio(undefined, titles.enableAll))
            chooseTitle.play()
        }
    }

    private chooseBackgroundScreen = () => {
        log.debug("Choose background screen")
        const backgrounds = this.sprites.create(MakeVideoSpriteTypes.BACKGROUND_SELECTION, {
            x: Scale.x(0.075),
            y: Scale.y(0.226),
            nextButton: this.nextButton,
            edit: this.edit
        }) as MakeVideoBackgroundSelectContainer

        this.getScreenAudio(backgrounds.disableAll, backgrounds.enableAll)
    }

    private reviewVideo = () => {
        log.debug("Review video")
        this.setEditButton()
        const video = this.sprites.create(MakeVideoSpriteTypes.VIDEO_PLAYER, {
            x: Scale.x(0.15),
            y: Scale.y(0.25),
            edit: this.edit,
            nextButton: this.nextButton,
            editButton: this.editButton
        }) as MakeVideoVideoPlayerContainer

        const onPlay = () => {
            if (!CCUtils.debug) {
                this.editButton.disable()
                video.disableAll()
            }
        }
        this.getScreenAudio(onPlay, video.enableAll)
    }

    private getScreenAudio = (onPlay?: () => void, onStop?: () => void) => {
        const audio = this.game.add.audio(this.screenAudioMap().get(this.step))
        if (onPlay) {
            audio.onPlay.addOnce(onPlay)
        }
        if (onStop) {
            audio.onStop.addOnce(onStop)
        }
        audio.play()
    }

    private setScreenComponents = () => {
        this.stepContainer = new MakeVideoStepsContainer(this.game)
        this.screenText = new MakeVideoScreenText(this.game)
        this.nextButton = Buttons.create(this.game, "Next", () => this.moveToStep(), {
            debug: CCUtils.debug,
            x: Scale.x(0.829),
            y: Scale.y(0.926),
            buttonType: ButtonType.SECONDARY,
            buttonSize: ButtonSize.WIDE
        })
    }

    private setEditButton = () => {
        const onClick = () => {
            this.edit = true
            log.debug("Editting video, moving to step 0")
            this.moveToStep(0)
        }
        this.editButton = Buttons.create(this.game, "Edit", onClick, {
            debug: CCUtils.debug,
            x: Scale.x(0.829),
            y: Scale.y(0.25),
            buttonType: ButtonType.SECONDARY,
            buttonSize: ButtonSize.WIDE
        })
    }

    private screenAudioMap = (): Map<number, MakeVideoAudio> =>
        this.edit
            ? new Map([
                [0, MakeVideoAudio.CHOOSE_ANOTHER_MUSIC],
                [1, MakeVideoAudio.CHOOSE_ANOTHER_VIDEO],
                [2, MakeVideoAudio.CHOOSE_ANOTHER_TITLE],
                [3, MakeVideoAudio.CLICK_NEW_BACKGROUND],
                [4, MakeVideoAudio.PLAY_EDITED_VIDEO],
            ])
            : new Map([
                [0, MakeVideoAudio.CHOOSE_MUSIC],
                [1, MakeVideoAudio.CHOOSE_VIDEO],
                [2, MakeVideoAudio.CLICK_TITLE],
                [3, MakeVideoAudio.CHOOSE_BACKGROUND],
                [4, MakeVideoAudio.PLAY_VIDEO],
            ])
}
